import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { FC, useEffect, useState } from 'react';
import { useDebounceCallback } from '../../../../../hooks/useDebounceValue';
import { useLocationFilter } from '../hooks/useLocationFilter';
import { styleSheet } from './styles';

interface LocationFiltersProps {
	className?: string;
	styles?: (StyleDeclarationValue | false)[];
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}

const DEBOUNCE_MS = 300;

export const LocationFilters: FC<LocationFiltersProps> = ({ className, styles = [], filter, onChangeFilter }) => {
	const { city, state, zip, setCity, setState, setZip } = useLocationFilter({
		filter,
		onChangeFilter,
	});

	// Local state for immediate input feedback
	const [localCity, setLocalCity] = useState(city);
	const [localState, setLocalState] = useState(state);
	const [localZip, setLocalZip] = useState(zip);

	// Sync local state when filter changes externally (e.g., loading saved search)
	useEffect(() => {
		setLocalCity(city);
	}, [city]);

	useEffect(() => {
		setLocalState(state);
	}, [state]);

	useEffect(() => {
		setLocalZip(zip);
	}, [zip]);

	// Debounced callbacks to avoid excessive API calls while typing
	const debouncedSetCity = useDebounceCallback((value: string) => setCity(value), DEBOUNCE_MS);
	const debouncedSetState = useDebounceCallback((value: string) => setState(value), DEBOUNCE_MS);
	const debouncedSetZip = useDebounceCallback((value: string) => setZip(value), DEBOUNCE_MS);

	const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocalCity(value);
		debouncedSetCity(value);
	};

	const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocalState(value);
		debouncedSetState(value);
	};

	const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setLocalZip(value);
		debouncedSetZip(value);
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='location-city' className={css(styleSheet.fieldLabel)}>
					City
				</label>
				<input
					id='location-city'
					type='text'
					className={css(styleSheet.input)}
					value={localCity}
					onChange={handleCityChange}
					placeholder='e.g., Boston'
					autoComplete='off'
				/>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='location-state' className={css(styleSheet.fieldLabel)}>
					State/Province
				</label>
				<input
					id='location-state'
					type='text'
					className={css(styleSheet.input)}
					value={localState}
					onChange={handleStateChange}
					placeholder='e.g., MA'
					autoComplete='off'
				/>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='location-zip' className={css(styleSheet.fieldLabel)}>
					Zip/Postal Code
				</label>
				<input
					id='location-zip'
					type='text'
					className={css(styleSheet.input)}
					value={localZip}
					onChange={handleZipChange}
					placeholder='e.g., 02101'
					autoComplete='off'
				/>
			</div>
		</div>
	);
};
